import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  supplierId: string;
  caseQuantity?: number;
  isSample?: boolean;
}

interface SupplierShippingInfo {
  supplierId: string;
  subtotal: number;
  shippingCost: number;
  minimumFreeShipping: number;
  minimumOrder: number;
  meetsMinimumOrder: boolean;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  itemCount: number;
  subtotal: number;
  shippingTotal: number;
  total: number;
  totalItems: number;
  totalUnits: number;
  supplierShippingDetails: SupplierShippingInfo[];
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    // Load cart from localStorage on initial render
    const savedCart = localStorage.getItem("cart");
    return savedCart ? JSON.parse(savedCart) : [];
  });

  const [supplierShippingDetails, setSupplierShippingDetails] = useState<SupplierShippingInfo[]>([]);
  const [supplierProfiles, setSupplierProfiles] = useState<Record<string, {
    minimumFreeShipping: number, 
    shippingCost: number,
    minimumOrder: number
  }>>({});

  // Calculate total number of items (cases)
  const itemCount = items.reduce((total, item) => total + item.quantity, 0);
  
  // Calculate total number of individual units
  const totalUnits = items.reduce((total, item) => total + (item.quantity * item.caseQuantity), 0);

  // Calculate subtotal
  const subtotal = items.reduce((total, item) => total + (item.price * item.quantity), 0);
  
  // Calculate shipping total based on supplier minimums
  const shippingTotal = supplierShippingDetails.reduce((total, supplier) => {
    return total + supplier.shippingCost;
  }, 0);
  
  // Calculate total
  const total = subtotal + shippingTotal;

  // Fetch supplier shipping information
  useEffect(() => {
    const fetchSupplierProfiles = async () => {
      // Get unique supplier IDs from cart items
      const supplierIds = [...new Set(items.map(item => item.supplierId))];
      
      if (supplierIds.length === 0) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, minimum_free_shipping, shipping_cost, minimum_order')
          .in('id', supplierIds);

        if (error) throw error;

        const profiles: Record<string, {minimumFreeShipping: number, shippingCost: number, minimumOrder: number}> = {};
        data.forEach(supplier => {
          profiles[supplier.id] = {
            minimumFreeShipping: supplier.minimum_free_shipping || 0,
            shippingCost: supplier.shipping_cost || 10,
            minimumOrder: supplier.minimum_order || 0
          };
        });

        setSupplierProfiles(profiles);
      } catch (error) {
        console.error('Error fetching supplier profiles:', error);
      }
    };

    fetchSupplierProfiles();
  }, [items]);

  // Update supplier shipping details whenever items change
  useEffect(() => {
    // Group items by supplier
    const supplierItems: Record<string, CartItem[]> = {};
    items.forEach(item => {
      if (!supplierItems[item.supplierId]) {
        supplierItems[item.supplierId] = [];
      }
      supplierItems[item.supplierId].push(item);
    });

    // For each supplier, calculate their subtotal and determine shipping cost
    const shippingInfo: SupplierShippingInfo[] = Object.entries(supplierItems).map(([supplierId, supplierItems]) => {
      // Calculate subtotal for this supplier
      const supplierSubtotal = supplierItems.reduce((total, item) => total + (item.price * item.quantity), 0);

      // Get shipping info from profile or use defaults
      const profile = supplierProfiles[supplierId] || { 
        minimumFreeShipping: 100, 
        shippingCost: 10,
        minimumOrder: 0
      };
      const minimumFreeShipping = profile.minimumFreeShipping;
      const baseShippingCost = profile.shippingCost;
      const minimumOrder = profile.minimumOrder;

      // Determine if this supplier's order qualifies for free shipping
      const shippingCost = supplierSubtotal >= minimumFreeShipping ? 0 : baseShippingCost;
      
      // Determine if meets minimum order
      const meetsMinimumOrder = supplierSubtotal >= minimumOrder;

      return {
        supplierId,
        subtotal: supplierSubtotal,
        shippingCost,
        minimumFreeShipping,
        minimumOrder,
        meetsMinimumOrder
      };
    });

    setSupplierShippingDetails(shippingInfo);
  }, [items, supplierProfiles]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(items));
  }, [items]);

  // Add item to cart
  const addToCart = (newItem: CartItem) => {
    setItems((prevCart) => {
      // Check if the item is already in the cart, considering if it's a sample
      const existingItemIndex = prevCart.findIndex(
        (item) => item.id === newItem.id && item.isSample === newItem.isSample
      );

      if (existingItemIndex !== -1) {
        // Item exists, update quantity
        const updatedCart = [...prevCart];
        updatedCart[existingItemIndex] = {
          ...updatedCart[existingItemIndex],
          quantity: updatedCart[existingItemIndex].quantity + newItem.quantity,
        };
        localStorage.setItem("cart", JSON.stringify(updatedCart));
        return updatedCart;
      } else {
        // Item doesn't exist, add it
        const updatedCart = [...prevCart, newItem];
        localStorage.setItem("cart", JSON.stringify(updatedCart));
        return updatedCart;
      }
    });
  };

  // Remove item from cart
  const removeFromCart = (id: string) => {
    setItems(prevItems => prevItems.filter(item => item.id !== id));
  };

  // Update item quantity
  const updateQuantity = (id: string, quantity: number) => {
    // Minimum quantity is 1
    if (quantity < 1) return;
    
    setItems(prevItems => {
      return prevItems.map(item => {
        if (item.id === id) {
          return { ...item, quantity };
        }
        return item;
      });
    });
  };

  // Clear cart
  const clearCart = () => {
    setItems([]);
  };

  return (
    <CartContext.Provider value={{ 
      items, 
      addToCart, 
      removeFromCart, 
      updateQuantity, 
      clearCart, 
      itemCount,
      subtotal,
      shippingTotal,
      total,
      totalItems: items.length,
      totalUnits,
      supplierShippingDetails
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
